import { useQuery } from "@tanstack/react-query";
import { Server } from "lucide-react";
import { EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function fmtDateTime(v: string | null) {
  if (!v) return "nikad";
  return new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Super Admin → WISP i SMS: samo pregled. Firme same dodaju svoje RADIUS servere i upisuju
 * Kannel/Yeastar parametre (odluka vlasnika 27.10.2026).
 */
export function WispAdminPage() {
  const q = useQuery({ queryKey: ["wisp-admin-overview"], queryFn: () => api.getWispAdminOverview(), refetchInterval: 15_000 });
  if (q.isLoading || !q.data) return <Skeleton className="h-60 w-full" />;
  const d = q.data;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">WISP i SMS</h1>
        <p className="text-sm text-slate-500">
          Pregled RADIUS servera svih firmi i SMS poruka ovog meseca. Podešavanja radi svaka firma sama (Internet korisnici → RADIUS serveri i
          SMS).
        </p>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="border-b border-border px-4 py-3 font-semibold text-slate-900">RADIUS serveri</div>
        {d.servers.length === 0 ? (
          <EmptyState icon={Server} title="Nijedna firma još nema RADIUS server" description="Pojaviće se ovde čim ga neka firma doda." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2">Firma</th>
                  <th className="px-3 py-2">Server</th>
                  <th className="px-3 py-2">Stanje</th>
                  <th className="px-3 py-2">Ruteri</th>
                  <th className="px-3 py-2">Pretplatnici</th>
                </tr>
              </thead>
              <tbody>
                {d.servers.map((s) => (
                  <tr key={s.id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{s.companyName}</div>
                      <div className="text-xs text-slate-500">{s.taxId ? `PIB ${s.taxId}` : ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div>{s.name}</div>
                      <div className="text-xs text-slate-500">{s.host ?? "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {s.online && s.radiusActive ? (
                        <span className="text-emerald-700">Radi · agent {s.version}</span>
                      ) : s.online ? (
                        <span className="text-amber-700">Agent radi, FreeRADIUS ne</span>
                      ) : (
                        <span className="text-red-700">Ne javlja se ({fmtDateTime(s.lastSeenAt)})</span>
                      )}
                      {s.lastError && <div className="text-red-700">{s.lastError}</div>}
                    </td>
                    <td className="px-3 py-2">{s.nasCount}</td>
                    <td className="px-3 py-2">{s.subscribers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <div className="border-b border-border px-4 py-3 font-semibold text-slate-900">SMS ovog meseca</div>
        {d.smsThisMonth.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-500">Još nema poslatih poruka.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {d.smsThisMonth.map((r) => (
                <tr key={r.companyName} className="border-t border-border first:border-t-0">
                  <td className="px-4 py-2">{r.companyName}</td>
                  <td className="px-4 py-2 text-emerald-700">poslato {r.sent}</td>
                  <td className="px-4 py-2 text-red-700">neuspelo {r.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
